import { Inject, Injectable, } from '@angular/core';
import { SettingsService } from '../../core/settings/settings.service';
import { HttpClient } from '@angular/common/http';
import { Observable, firstValueFrom } from 'rxjs';
import { AppConfigService } from '../../shared/appconfig/appconfig.service'
import { KendoDialogService } from '../../shared/services/kendo-dialog.service';

import { Bot } from '../models/bot.model';
import { DialogGraph, DialogNode, DialogLink, NameValueItem } from '../models/dialogs.model';

@Injectable({
  providedIn: 'root'
})
export class DialogService {

  static readonly SYSTEM_OWNER = 'system';

  public bot: Bot;
  public graph: DialogGraph;
  public node: DialogNode;
  public link: DialogLink;
  public listener: IDialogServiceListener;

  public owners: NameValueItem[];
  public linkGraphs: NameValueItem[];
  public linkNodes: NameValueItem[];

  public linkSelectedOwner: string;
  public linkSelectedGraph: string;
  public linkSelectedNode: string;

  public debugging: boolean;

  public working: boolean;
  public isDirty: boolean;

  // SuperAdmin: when set, the IDE reads/writes the shared template (_Tenants.Dialogs)
  // for this owner instead of the current bot's merged dialogs. null = normal mode.
  public templateOwner: string | null = null;

  getUrl(): string {
    return AppConfigService.settings.get('chatstyleapiServer');
  }

  constructor(
    public settings: SettingsService,
    private http: HttpClient,
    private kendoDialog: KendoDialogService,
    @Inject('BASE_URL') baseUrl: string) {
    this.debugging = false;
    this.working = false;
  }

  hasSelection(): boolean {
    if (this.debugging) return false;
    if (this.node) return true;
    if (this.link) return true;
    return false;
  }

  clear() {
    console.log('clear');

    if (!this.working) {
      if (this.node || this.link) {
        this.settings.setLayoutSetting('nodeSidebarOpen', false);
        this.bot = null;
        this.node = null;
        this.link = null;

        this.linkSelectedOwner = null;
        this.linkSelectedGraph = null;
        this.linkSelectedNode = null;

        if (this.listener) {
          this.listener.onClear();
        }
      }
   }

  }

  setDirty(val: boolean) {
    this.isDirty = val;
    console.log('setting dlgservice.dirty=' + this.isDirty);
  }

  setBot(b: Bot) {
    this.bot = b;
    this.loadOwners();
  }


  loadOwners() {
    this.owners = [];

    if (this.bot) {
      const b: NameValueItem = {
        display: 'Bot',
        value: this.bot.botID
      };
      this.owners.push(b);
    }

    const s: NameValueItem = {
      display: 'System',
      value: DialogService.SYSTEM_OWNER
    };
    this.owners.push(s);
  }

  loadLinkedGraphs() {
    console.log('loadLinkedGraphs');
    this.getGraphIds().then((result) => {

      let filterGraph = this.graph.dialogGraphID;
      let validList = result.filter(function (obj) {
        return obj.value !== filterGraph;
      });

      this.linkGraphs = validList;
      if (validList && validList.length > 0 && !this.linkSelectedGraph) {
        this.linkSelectedGraph = validList[0].value;
      }

      this.loadLinkedNodes();
    });
  }

  loadLinkedNodes() {
    console.log('loadLinkedNodes');
    this.getNodeIds(this.linkSelectedGraph).then((result) => {

      this.linkNodes = result;
      if (this.linkNodes && this.linkNodes.length > 0 && !this.linkSelectedNode) {
        this.linkSelectedNode = this.linkNodes[0].value;
      }

      this.repointLinksToNode(this.node, this.linkSelectedOwner,
        this.linkSelectedGraph, this.linkSelectedNode);

      this.node.ownerID = this.linkSelectedOwner;
      this.node.parentGraphID = this.linkSelectedGraph;
      this.node.dialogNodeID = this.linkSelectedNode;

      this.setDirty(true);
    });
  }

  selectGraph(g) {
    this.graph = g;
  }

  selectNode(n) {
    this.working = false;
    this.node = n;
    this.link = null;

    console.log(n);
    this.linkGraphs = [];
    this.linkNodes = [];

    if (this.node && this.node.type == 'link') {
      this.linkSelectedOwner = this.node.ownerID;
      this.linkSelectedGraph = this.node.parentGraphID;
      this.linkSelectedNode = this.node.dialogNodeID;

      this.loadLinkedGraphs();
    }

    if (!this.debugging) {
      this.settings.setLayoutSetting('nodeSidebarOpen', true);
    }
  };

  selectLink(l) {
    this.working = false;
    this.link = l;
    this.node = null;

    console.log(l);

    if (!this.debugging) {

      this.settings.setLayoutSetting('nodeSidebarOpen', true);
    }
  };

  saveGraph(graph: DialogGraph): Promise<DialogGraph> {
    if (this.templateOwner) {
      return firstValueFrom(this.saveTemplateDialog(this.templateOwner, graph)).then(r => this.fromPayload(r));
    }
    const payload = this.toPayload(graph);
    return this.http
      .post<any>(this.getUrl() + 'dialog', payload).toPromise()
      .then(r => this.fromPayload(r));
  };

  addGraph(graph: DialogGraph): Promise<DialogGraph> {
    if (this.templateOwner) {
      return firstValueFrom(this.saveTemplateDialog(this.templateOwner, graph)).then(r => this.fromPayload(r));
    }
    const payload = this.toPayload(graph);
    return this.http
      .put<any>(this.getUrl() + 'dialog', payload).toPromise()
      .then(r => this.fromPayload(r));
  };

  deleteGraph(dialogId: number): Promise<DialogGraph> {
    if (this.templateOwner) {
      return firstValueFrom(this.deleteTemplateDialog(dialogId)).then(() => null as any);
    }
    return this.http
      .delete<DialogGraph>(this.getUrl() + 'dialog/' + dialogId).toPromise();
  };

  getGraphs(owner?: string): Promise<DialogGraph[]> {
    if (this.templateOwner) {
      return firstValueFrom(this.getTemplateDialogs(this.templateOwner, false))
        .then(items => (items ?? []).map(r => this.fromPayload(r)));
    }
    return this.http.get<any[]>(this.getUrl() + 'dialog').toPromise()
      .then(items => (items ?? []).map(r => this.fromPayload(r)));
  }

  getGraphsInternal(): Promise<DialogGraph[]> {
    if (this.templateOwner) {
      return firstValueFrom(this.getTemplateDialogs(this.templateOwner, true))
        .then(items => (items ?? []).map(r => this.fromPayload(r)));
    }
    return this.http.get<any[]>(this.getUrl() + 'dialog/internal').toPromise()
      .then(items => (items ?? []).map(r => this.fromPayload(r)));
  }

  getSystemGraphIds(): Promise<NameValueItem[]> {
    return this.http
      .get<NameValueItem[]>(this.getUrl() + 'dialog/' + DialogService.SYSTEM_OWNER + '/graphids')
      .toPromise();
  }

  getSystemNodeIds(graphId: string): Promise<NameValueItem[]> {
    return this.http
      .get<NameValueItem[]>(this.getUrl() + 'dialog/' + DialogService.SYSTEM_OWNER + '/' + graphId + '/nodeids')
      .toPromise();
  }

  getGraphIds(): Promise<NameValueItem[]> {

    return this.http
      .get<NameValueItem[]>(this.getUrl() + 'dialog/graphids').toPromise();
  }

  // --- SuperAdmin: edit the shared template (_Tenants.Dialogs) for a chosen owner ---
  // New methods return Observables per the portal's RxJS convention; the legacy IDE
  // methods above still use Promises and are migrated opportunistically.

  getDialogOwners(): Observable<string[]> {
    return this.http.get<string[]>(this.getUrl() + 'dialog/owners');
  }

  getTemplateDialogs(ownerId: string, includeSystem: boolean = false): Observable<any[]> {
    return this.http.get<any[]>(
      this.getUrl() + 'dialog/templates/' + encodeURIComponent(ownerId) + '?includeSystem=' + includeSystem
    );
  }

  saveTemplateDialog(ownerId: string, graph: DialogGraph): Observable<any> {
    return this.http.post<any>(
      this.getUrl() + 'dialog/templates/' + encodeURIComponent(ownerId),
      this.toPayload(graph)
    );
  }

  deleteTemplateDialog(dialogId: number): Observable<void> {
    return this.http.delete<void>(this.getUrl() + 'dialog/templates/' + dialogId);
  }

  getNodeIds(graph: string): Promise<NameValueItem[]> {

    return this.http
      .get<NameValueItem[]>(this.getUrl() + 'dialog/' + graph + '/nodeids').toPromise();
  }

  emptyGraph(): DialogGraph {
    return {
      botID: '',
      dialogGraphID: '',
      version: '',
      label: '',
      description: '',
      rootNodeID: '',
      nodes: [],
      links: [],
      x: 0,
      y: 0,
      aiEnabled: false,
      aiDescription: '',
      parametersJson: '',
      aiPriority: 0
    };
  }

  emptyNode(): DialogNode {
    const node: DialogNode = {
      ownerID: '',
      dialogNodeID: '', //this.selectedGraph.dialogGraphID + "-" + this.selectedGraph.nodes.length,
      parentGraphID: '',
      type: '',
      label: '',
      text: '',
      resourceID: '',
      options: [],
      preScript: '',
      postScript: '',
      errorScript: '',
      noMatchResponse: '',
      script: '',
      delay: 0,
      active: true,
      links: [],
      x: 100,
      y: 100,
      maxExecutions: 0,
      blockInput: false,
      blockAttachments: false,
      overrideID: '',
      timeToLive: 0,
      data: '',
      locking: false
    };
    return node;
  }

  removeNodeFromGraph(graph: DialogGraph, nodeToRemove: DialogNode): DialogGraph {

    // remove all the links to the node to remove
    graph.nodes.forEach((n) => {
      if (n) {
        if (n.links && n.links.length > 0) {
          const link = n.links.find(l => l.targetNodeID === nodeToRemove.dialogNodeID &&
            l.targetGraphID === nodeToRemove.parentGraphID);
          if (link) {
            console.log('removing link ' + link.dialogLinkID);

            const index = n.links.indexOf(link, 0);
            if (index > -1) {
              n.links.splice(index, 1);
            }
          }
        }
      }
    });
    const index = graph.nodes.indexOf(nodeToRemove, 0);
    if (index > -1) {
      graph.nodes.splice(index, 1);
    }

    return graph;
  };


  repointLinksToNode(nodeToRepoint: DialogNode, newOwner: string, newGraph: string, newNode: string): DialogGraph {
    console.log(nodeToRepoint);
    console.log('repoint to ' + newOwner + '-' + newGraph + '-' + newNode);

    // remove all the links to the node to remove
    this.graph.nodes.forEach((n) => {
      if (n) {
        if (n.links && n.links.length > 0) {
          const link = n.links.find(l => l.targetNodeID === nodeToRepoint.dialogNodeID &&
            l.targetGraphID === nodeToRepoint.parentGraphID);
          if (link) {
            console.log('repointing link ' + link.dialogLinkID);

            link.targetOwnerID = newOwner;
            link.targetGraphID = newGraph;
            link.targetNodeID = newNode;
          }
        }
      }
    });

    return this.graph;
  };


  deleteSelection() {
    this.working = true;
    let msg = 'Are you sure you want to delete this ';
    if (this.node) {
      msg += ' node and all associated links?';
    } else {
      msg += ' link?';
    }

    this.kendoDialog.confirm({
      title: 'Confirm Delete',
      message: msg,
      confirmText: 'Yes, delete it!',
      cancelText: 'Cancel'
    }).subscribe((confirmed) => {
      if (confirmed) {
        this.setDirty(true);

        if (this.node) {
          this.removeNodeFromGraph(this.graph, this.node);
        } else {
          if (this.link) {
            const id = this.link.dialogLinkID;

            for (var i = 0; i < this.graph.nodes.length; i++) {
              let n = this.graph.nodes[i];
              const index = n.links.indexOf(this.link, 0);
              if (index > -1) {
                n.links.splice(index, 1);
              }
            }
          }
        }
        this.working = false;
        this.clear();
        if (this.listener) {
          this.listener.onUpdate();
        }
      } else {
        this.working = false;
      }
    });
  };

  private toPayload(graph: DialogGraph): any {
    const { nodes, links, ...rest } = graph as any;
    const serializedNodes = (nodes || []).map((n: any) => {
      const { links: nodeLinks, ...nodeRest } = n;
      const l0 = n.type === 'link' ? nodeLinks?.[0] : null;
      return {
        ...nodeRest,
        parentGraphID: l0?.targetGraphID || nodeRest.parentGraphID || graph.dialogGraphID,
        dialogNodeID:  l0?.targetNodeID  || nodeRest.dialogNodeID,
        ownerID:       l0?.targetOwnerID || nodeRest.ownerID,
        linksJson: JSON.stringify(nodeLinks || [])
      };
    });
    return {
      ...rest,
      nodesJson: JSON.stringify(serializedNodes),
      linksJson: JSON.stringify(links || [])
    };
  }

  private parseJson<T>(json: string, fallback: T): T {
    if (!json) return fallback;
    try { return JSON.parse(json) as T; } catch { return fallback; }
  }

  private static readonly BEHAVIOR_NAMES = ['Never', 'Always', 'Best', 'Payload', 'NoMatch', 'Error', 'BadLoop', 'Condition', 'Pattern', 'NodeComplete', 'Expired', 'Locked', 'AiPath'];

  private normalizeLink(l: any): any {
    const n = this.normalizeKeys(l);
    // Coerce legacy camelCase Id → canonical ID suffix (mirrors fromPayload graph-level handling)
    n.dialogLinkID  = n.dialogLinkID  ?? n.dialogLinkId;
    n.targetNodeID  = n.targetNodeID  ?? n.targetNodeId;
    n.targetGraphID = n.targetGraphID ?? n.targetGraphId;
    n.targetOwnerID = n.targetOwnerID ?? n.targetOwnerId;
    if (n.label === 'New Link') n.label = '';

    if (typeof n.behavior === 'number') {
      n.behavior = DialogService.BEHAVIOR_NAMES[n.behavior] ?? 'Always';
    } else if (typeof n.behavior === 'string' && n.behavior) {
      n.behavior = n.behavior.charAt(0).toUpperCase() + n.behavior.slice(1);
    }
    if (Array.isArray(n.match)) {
      n.match = n.match.map((m: any) => this.normalizeKeys(m));
    }
    if (n.behavior === 'Payload' && !n.payload && n.match?.length > 0) {
      n.payload = n.match[0].value ?? n.match[0].Value ?? '';
    }
    return n;
  }

  private normalizeKeys(obj: any): any {
    if (!obj || typeof obj !== 'object') return obj;
    const result: any = {};
    for (const key of Object.keys(obj)) {
      result[key.charAt(0).toLowerCase() + key.slice(1)] = obj[key];
    }
    return result;
  }

  private fromPayload(raw: any): DialogGraph {
    const { nodesJson, linksJson, ...rest } = raw;
    const rawNodes: any[] = this.parseJson(nodesJson, rest.nodes ?? []) ?? [];
    const nodes = rawNodes.map((n: any) => {
      const normalized = this.normalizeKeys(n);
      // Coerce legacy camelCase Id → canonical ID suffix
      normalized.dialogNodeID  = normalized.dialogNodeID  ?? normalized.dialogNodeId;
      normalized.parentGraphID = normalized.parentGraphID ?? normalized.parentGraphId;
      normalized.overrideID    = normalized.overrideID    ?? normalized.overrideId;
      const nodeLinksJson = normalized.linksJson;
      const { linksJson: _lj, links: _la, ...nodeRest } = normalized;
      const rawLinks: any[] = (nodeLinksJson ? this.parseJson(nodeLinksJson, _la || []) : (_la || [])) ?? [];
      if (nodeRest.type === 'link' && rawLinks.length === 0 && nodeRest.parentGraphID) {
        rawLinks.push({ behavior: 'Always', active: true,
          targetGraphID: nodeRest.parentGraphID,
          targetNodeID:  nodeRest.dialogNodeID,
          targetOwnerID: nodeRest.ownerID });
      }
      return {
        ...nodeRest,
        links: rawLinks.map(l => this.normalizeLink(l))
      };
    });
    return {
      ...rest,
      dialogGraphID: rest.dialogGraphID ?? rest.dialogGraphId,
      rootNodeID: rest.rootNodeID ?? rest.rootNodeId,
      botID: rest.botID ?? rest.ownerId,
      nodes,
      links: ((this.parseJson(linksJson, rest.links ?? []) ?? []) as any[]).map(l => this.normalizeLink(l))
    } as DialogGraph;
  }

}



export interface IDialogServiceListener {
  onClear();
  onUpdate();
};
